'use client';

import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { sectionHeadingTag } from '@/components/dynamic-zone/section-heading-level';
import MediaCarousel from '@/components/media-carousel/MediaCarousel';
import { cn } from '@/lib/utils';
import { components } from '@/types/strapi';

export default function TwoColumnWithAccordion({
	heading = 'Why should Businesses use WhatsApp Chatbot?',
	subheading = 'Enhance your customer engagement with intelligent automated responses',
	images,
	items = [
		{
			title: '24/7 Availability',
			content:
				'With a WhatsApp bot for business, you can provide round-the-clock support to your customers, ensuring they receive timely responses regardless of the hour.',
		},
		{
			title: 'Increased Engagement',
			content:
				'Integrating a chatbot in WhatsApp enables you to interact with customers in real time, fostering better engagement and satisfaction.',
		},
		{
			title: 'Cost-Effective Solutions',
			content:
				'Automate routine inquiries and support tasks, reducing operational costs while maintaining high-quality customer service.',
		},
		{
			title: 'Personalized Interactions',
			content:
				'Deliver customized responses based on user history and preferences, creating more meaningful conversations.',
		},
		{
			title: 'Seamless Integration',
			content:
				'Easily integrate with your existing business systems and workflows for efficient operation.',
		},
	],
	backgroundColor = 'bg-gradient-to-b from-gray-50 to-white',
	isFirst = false,
}: components['schemas']['DynamicZoneTwoColumnWithAccordionComponent'] & {
	isFirst?: boolean;
}) {
	const SectionHeading = sectionHeadingTag(isFirst);
	return (
		<section
			data-testid="two-column-with-accordion"
			className={cn(
				'two-column-with-accordion-section',
				backgroundColor,
				'py-16 sm:py-24',
			)}>
			<noscript>
				<style>{`
					.two-column-with-accordion-section h2[style],
					.two-column-with-accordion-section h3[style],
					.two-column-with-accordion-section p[style],
					.two-column-with-accordion-section div[style*='opacity'],
					.two-column-with-accordion-section div[style*='scale'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				{/* Header */}
				<motion.div
					data-testid="two-column-with-accordion-header"
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					className="mx-auto mb-16 max-w-3xl text-center">
					<SectionHeading className="mb-4 bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-3xl font-bold text-transparent sm:text-4xl lg:text-5xl">
						{heading}
					</SectionHeading>
					<p className="text-lg text-gray-600/90 sm:text-xl">
						{subheading}
					</p>
				</motion.div>

				{/* Content Grid */}
				<div
					className={cn(
						'grid grid-cols-1 gap-x-12 gap-y-8',
						images && 'lg:grid-cols-2',
					)}>
					{/* Left Column - Fixed Image */}
					{images && (
						<motion.div
							data-testid="two-column-with-accordion-image"
							initial={{ opacity: 0, x: -20 }}
							whileInView={{ opacity: 1, x: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.5 }}
							className="relative lg:sticky lg:top-24">
							<div className="overflow-hidden rounded-3xl bg-gradient-to-tr from-indigo-50 to-blue-50 p-1 shadow-xl">
								<div className="relative aspect-[4/3]">
									<MediaCarousel images={images} />
									{/* Decorative Elements */}
									<div className="absolute right-6 top-6 flex space-x-2">
										{[...Array(3)].map((_, i) => (
											<motion.div
												key={i}
												className="h-2 w-2 rounded-full bg-indigo-600/40"
												animate={{
													opacity: [0.4, 1, 0.4],
													scale: [1, 1.2, 1],
												}}
												transition={{
													duration: 1.5,
													repeat: Infinity,
													delay: i * 0.2,
												}}
											/>
										))}
									</div>
									<div className="absolute -right-10 -top-10 h-40 w-40 animate-pulse rounded-full bg-indigo-500/10 blur-3xl" />
									<div className="absolute -bottom-12 -left-12 h-48 w-48 animate-pulse rounded-full bg-blue-500/10 blur-3xl" />
								</div>
							</div>
						</motion.div>
					)}

					{/* Right Column - Accordion */}
					<div
						data-testid="two-column-with-accordion-content"
						className={cn(
							'flex max-w-[650px] flex-col space-y-4',
							!images && 'mx-auto',
						)}>
						{items.map((item, index) => (
							<motion.div
								key={index}
								data-testid={`accordion-item-${index}`}
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true }}
								transition={{
									duration: 0.5,
									delay: index * 0.1,
								}}>
								<details className="group rounded-2xl border border-gray-100 bg-white shadow-sm transition hover:shadow-md">
									<summary
										data-testid={`accordion-item-${index}-summary`}
										className="flex cursor-pointer list-none items-center justify-between gap-4 p-5 transition-colors hover:bg-gray-50">
										<div className="flex items-center gap-4">
											<div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-indigo-50 transition-colors group-open:bg-indigo-100">
												<span className="font-bold text-indigo-600">
													{index + 1}
												</span>
											</div>
											<h3 className="text-base font-semibold text-gray-900 transition-colors group-hover:text-indigo-600">
												{item.title}
											</h3>
										</div>
										<ChevronDown className="h-5 w-5 text-gray-400 transition-transform duration-300 group-open:rotate-180 group-hover:text-indigo-600" />
									</summary>
									<div
										data-testid={`accordion-item-${index}-content`}
										className="p-5">
										<p className="text-sm leading-relaxed text-gray-600">
											{item.content}
										</p>
									</div>
								</details>
							</motion.div>
						))}
					</div>
				</div>
			</div>
		</section>
	);
}
